// Change-password screen, reached from the account dropdown in the header.
//
// This is the only in-app way to change a password: loco's /forgot + /reset
// pair delivers its token by email and the mailer is disabled in production.

import { useState } from 'react';
import { Alert, Button, Card, Form, Input, Typography, message } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { ApiError, sendJson } from '../api/rest';
import { useAuth } from '../auth/AuthContext';

const MIN_LENGTH = 12;

interface ChangePasswordValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export default function ChangePassword() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [form] = Form.useForm<ChangePasswordValues>();
  const [submitting, setSubmitting] = useState(false);

  const onFinish = async (values: ChangePasswordValues) => {
    setSubmitting(true);
    try {
      await sendJson('POST', '/api/auth/change-password', {
        current_password: values.currentPassword,
        new_password: values.newPassword,
      });
      // The old JWT stays valid until it expires, but staying signed in after
      // a password change is surprising — force a fresh login so the new
      // credential is the one actually in use.
      message.success('密码已修改，请用新密码重新登录');
      logout();
      navigate('/login', { replace: true });
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.status === 401
            ? '当前密码不正确'
            : err.message
          : err instanceof Error
            ? err.message
            : '修改失败';
      message.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ maxWidth: 480, margin: '32px auto', padding: '0 16px' }}>
      <Card>
        <Typography.Title level={4} style={{ marginTop: 0 }}>
          修改密码
        </Typography.Title>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={`新密码至少 ${MIN_LENGTH} 位，修改后需要重新登录。`}
        />
        <Form form={form} layout="vertical" onFinish={onFinish} requiredMark={false}>
          <Form.Item
            name="currentPassword"
            label="当前密码"
            rules={[{ required: true, message: '请输入当前密码' }]}
          >
            <Input.Password prefix={<LockOutlined />} autoComplete="current-password" />
          </Form.Item>

          <Form.Item
            name="newPassword"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: MIN_LENGTH, message: `至少 ${MIN_LENGTH} 位` },
            ]}
          >
            <Input.Password prefix={<LockOutlined />} autoComplete="new-password" />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="确认新密码"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: '请再次输入新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined />} autoComplete="new-password" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" loading={submitting} block>
              提交
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}
